import { useEffect, useMemo, useRef, useState, type ChangeEvent, type JSX, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import {
  MATERIALS_OWNERSHIP_LABELS,
  type ClientOfferClientDetails,
  type MaterialsOwnership,
  type OfferValidityDays,
  type Step3OfferExtras,
  type Step3ProjectMilestone,
  type Step3ScheduleStage,
} from "@/types/mvpEstimate";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";
import {
  createDefaultAssumptionRows,
  DEFAULT_ASSUMPTIONS,
  DEFAULT_PAYMENT_TEXT,
  DEFAULT_WARRANTY_TEXT,
  normalizeStep3SectionIncludes,
  resolveInitialAssumptionRows,
  savedAssumptionDataIsStale,
  WARRANTY_PRESET_TEXT,
  type Step3SectionIncludes,
} from "@/components/mvp/step3OfferTerms";
import styles from "./MvpStep3ClientConditions.module.css";

const MATERIALS_OPTIONS: MaterialsOwnership[] = ["client", "contractor", "split", "tbd"];
const VALIDITY_OPTIONS: OfferValidityDays[] = ["7", "14", "30"];

const COMPANY_ADVANTAGE_PLACEHOLDERS = [
  "np. Gwarancja terminowości",
  "np. Własne materiały w konkurencyjnych cenach",
  "np. Doświadczenie w wykończeniach premium",
] as const;

export {
  DEFAULT_ASSUMPTIONS,
  DEFAULT_ASSUMPTIONS as DEFAULT_OFFER_ASSUMPTIONS,
  createDefaultAssumptionRows,
  resolveInitialAssumptionRows,
  savedAssumptionDataIsStale,
} from "./step3OfferTerms";

function autoResizeTextarea(element: HTMLTextAreaElement) {
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
}

function textToList(value: string): string[] | undefined {
  const list = value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

function AutoResizeTextarea({
  value,
  onChange,
  maxLength,
  placeholder,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (ref.current) autoResizeTextarea(ref.current);
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    autoResizeTextarea(e.currentTarget);
    onChange(e.target.value);
  };

  return (
    <textarea
      ref={ref}
      className={styles.textarea}
      value={value}
      rows={2}
      maxLength={maxLength}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={handleChange}
    />
  );
}

function Section({
  title,
  badge,
  collapsible,
  included,
  onIncludedChange,
  children,
}: {
  title: string;
  badge?: string;
  collapsible?: boolean;
  included: boolean;
  onIncludedChange: (next: boolean) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!collapsible);
  const headingId = `step3-${title.replace(/\s+/g, "-").toLowerCase()}-heading`;

  return (
    <section
      className={`${styles.cardFull} ${!included ? styles.cardExcluded : ""}`}
      aria-labelledby={headingId}
    >
      <div className={styles.cardHeader}>
        <div className={styles.titleRow}>
          <span id={headingId} className={styles.sectionLabel}>
            {title}
          </span>
          {badge ? <span className={styles.badge}>{badge}</span> : null}
        </div>
        <div className={styles.headerActions}>
          <label className={styles.includeToggle}>
            <button
              type="button"
              role="switch"
              aria-checked={included}
              aria-label={`${included ? "Uwzględnij w ofercie" : "Pominięto w ofercie"}: ${title}`}
              className={`${styles.offerSwitch} ${included ? styles.offerSwitchOn : ""}`}
              onClick={() => onIncludedChange(!included)}
            />
            <span className={styles.includeToggleLabel}>
              {included ? "Uwzględnij w ofercie" : "Pominięto w ofercie"}
            </span>
          </label>
          {collapsible ? (
            <button type="button" className={styles.smallButton} onClick={() => setOpen((value) => !value)}>
              {open ? "Zwin" : "Rozwin"}
            </button>
          ) : null}
        </div>
      </div>
      <div className={`${styles.collapsible} ${open ? styles.collapsibleOpen : ""}`}>
        <div className={`${styles.cardBody} ${!included ? styles.cardBodyExcluded : ""}`}>{children}</div>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className={styles.fieldBlock}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

export type MvpStep3ClientConditionsProps = {
  clientDetails?: ClientOfferClientDetails;
  onClientFieldChange: (field: "name" | "address" | "phone" | "email", value: string) => void;
  materialsOwnership?: MaterialsOwnership;
  onMaterialsOwnershipChange: (value: MaterialsOwnership | undefined) => void;
  warrantyType?: string;
  onWarrantyTypeChange: (value: string | undefined) => void;
  paymentTerms?: string;
  onPaymentTermsChange: (value: string | undefined) => void;
  templateType?: TemplateType;
  timelineNote?: string;
  onTimelineNoteChange: (value: string | undefined) => void;
  exclusions?: string[];
  onExclusionsChange: (next: string[] | undefined) => void;
  offerAssumptions?: string[];
  onOfferAssumptionsChange: (next: string[] | undefined) => void;
  step3SectionIncludes?: Step3SectionIncludes;
  onStep3SectionIncludesChange: (next: Step3SectionIncludes) => void;
  step3Extras?: Step3OfferExtras;
  onStep3ExtrasChange: (patch: Partial<Step3OfferExtras>) => void;
  step4ClientError?: string | null;
  step3DraftLimit?: { used: number } | null;
};

export function MvpStep3ClientConditions({
  clientDetails,
  onClientFieldChange,
  materialsOwnership,
  onMaterialsOwnershipChange,
  warrantyType,
  onWarrantyTypeChange,
  paymentTerms,
  onPaymentTermsChange,
  templateType = "quick",
  timelineNote,
  onTimelineNoteChange,
  exclusions,
  onExclusionsChange,
  offerAssumptions,
  onOfferAssumptionsChange,
  step3SectionIncludes,
  onStep3SectionIncludesChange,
  step3Extras,
  onStep3ExtrasChange,
  step4ClientError,
  step3DraftLimit,
}: MvpStep3ClientConditionsProps): JSX.Element {
  const includes = normalizeStep3SectionIncludes(step3SectionIncludes, templateType);
  const showExplained = templateType === "explained" || templateType === "premium";
  const showPremium = templateType === "premium";
  const assumptionsText = step3Extras?.assumptionsText ?? (offerAssumptions ?? exclusions ?? []).join("\n");
  const warrantyText = warrantyType ?? DEFAULT_WARRANTY_TEXT;
  const paymentText = paymentTerms ?? DEFAULT_PAYMENT_TEXT;
  const stages = step3Extras?.scheduleStages ?? [];
  const kamienieMilowe = step3Extras?.projectMilestones ?? [];

  const activeWarrantyPreset = useMemo(() => {
    for (const months of [12, 24, 36] as const) {
      if (warrantyText.trim() === WARRANTY_PRESET_TEXT[months]) return months;
    }
    return null;
  }, [warrantyText]);

  const setInclude = (key: keyof Step3SectionIncludes, value: boolean) => {
    onStep3SectionIncludesChange({ ...includes, [key]: value });
  };

  const conditionsIncluded =
    includes.materials && includes.payment && includes.warranty && includes.assumptions;

  const setConditionsIncluded = (value: boolean) => {
    onStep3SectionIncludesChange({
      ...includes,
      materials: value,
      payment: value,
      warranty: value,
      assumptions: value,
    });
  };

  const patchExtras = (patch: Partial<Step3OfferExtras>) => onStep3ExtrasChange(patch);

  const updateStage = (id: string, patch: Partial<Step3ScheduleStage>) => {
    patchExtras({ scheduleStages: stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)) });
  };

  const addStage = () => {
    if (stages.length >= 6) return;
    patchExtras({ scheduleStages: [...stages, { id: crypto.randomUUID(), name: "", duration: "" }] });
  };

  const updateKamienMilowy = (id: string, patch: Partial<Step3ProjectMilestone>) => {
    patchExtras({
      projectMilestones: kamienieMilowe.map((kamienMilowy) =>
        kamienMilowy.id === id ? { ...kamienMilowy, ...patch } : kamienMilowy
      ),
    });
  };

  const addKamienMilowy = () => {
    if (kamienieMilowe.length >= 8) return;
    patchExtras({
      projectMilestones: [...kamienieMilowe, { id: crypto.randomUUID(), name: "", dateOrWeek: "", payment: "" }],
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.grid}>
        <Section
          title="Dane klienta"
          included={includes.clientDetails}
          onIncludedChange={(value) => setInclude("clientDetails", value)}
        >
          <div className={styles.fieldGrid}>
            <Field label="Imie / firma">
              <input className={styles.input} placeholder="np. Jan Kowalski / Firma Remontowa XYZ" value={clientDetails?.name ?? ""} onChange={(e) => onClientFieldChange("name", e.target.value)} autoComplete="name" />
            </Field>
            <Field label="Adres inwestycji">
              <input className={styles.input} placeholder="np. ul. Królewska 22 m. 4, Warszawa" value={clientDetails?.address ?? ""} onChange={(e) => onClientFieldChange("address", e.target.value)} autoComplete="street-address" />
            </Field>
            <div className={styles.fieldRow2}>
              <Field label="Telefon">
                <input className={styles.input} type="tel" placeholder="np. 600 123 456" value={clientDetails?.phone ?? ""} onChange={(e) => onClientFieldChange("phone", e.target.value)} autoComplete="tel" />
              </Field>
              <Field label="Email">
                <input className={styles.input} type="email" placeholder="np. jan.kowalski@gmail.com" value={clientDetails?.email ?? ""} onChange={(e) => onClientFieldChange("email", e.target.value)} autoComplete="email" />
              </Field>
            </div>
          </div>
        </Section>

        <Section
          title="Warunki"
          included={conditionsIncluded}
          onIncludedChange={setConditionsIncluded}
        >
          <div className={styles.fieldGrid}>
            <Field label="Materialy">
              <select className={styles.input} value={materialsOwnership ?? ""} onChange={(e) => onMaterialsOwnershipChange((e.target.value || undefined) as MaterialsOwnership | undefined)}>
                <option value="">Do wyboru</option>
                {MATERIALS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {MATERIALS_OWNERSHIP_LABELS[option]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Waznosc oferty">
              <select className={styles.input} value={step3Extras?.offerValidityDays ?? "14"} onChange={(e) => patchExtras({ offerValidityDays: e.target.value as OfferValidityDays })}>
                {VALIDITY_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    {days} dni
                  </option>
                ))}
              </select>
            </Field>
            <div className={styles.fieldRow2}>
              <Field label="Termin startu">
                <input className={styles.input} placeholder="np. sierpień 2025 / po uzgodnieniu" value={step3Extras?.startTerm ?? ""} onChange={(e) => patchExtras({ startTerm: e.target.value })} />
              </Field>
              <Field label="Czas realizacji">
                <input className={styles.input} placeholder="np. 2–3 tygodnie" value={step3Extras?.completionTime ?? ""} onChange={(e) => patchExtras({ completionTime: e.target.value })} />
              </Field>
            </div>
            <Field label="Warunki platnosci">
              <AutoResizeTextarea value={paymentText} onChange={(value) => onPaymentTermsChange(value.trim() ? value : undefined)} />
            </Field>
            <div className={styles.fieldBlock}>
              <span className={styles.fieldLabel}>Gwarancja</span>
              <div className={styles.chipRow}>
                {([12, 24, 36] as const).map((months) => (
                  <button key={months} type="button" className={`${styles.chip} ${activeWarrantyPreset === months ? styles.chipActive : ""}`} onClick={() => onWarrantyTypeChange(WARRANTY_PRESET_TEXT[months])}>
                    {months} mies.
                  </button>
                ))}
              </div>
              <AutoResizeTextarea value={warrantyText} onChange={(value) => onWarrantyTypeChange(value.trim() ? value : undefined)} />
            </div>
            <Field label="Dodatkowy opis gwarancji">
              <AutoResizeTextarea value={step3Extras?.warrantyNotes ?? ""} onChange={(value) => patchExtras({ warrantyNotes: value })} />
            </Field>
            <Field label="Wylaczenia i zalozenia">
              <AutoResizeTextarea
                value={assumptionsText}
                onChange={(value) => {
                  patchExtras({ assumptionsText: value });
                  onOfferAssumptionsChange(textToList(value));
                  onExclusionsChange(undefined);
                }}
              />
            </Field>
          </div>
        </Section>

        {showExplained ? (
          <>
            <Section
              title="O nas"
              badge="Przekonujaca i Reprezentacyjna"
              collapsible
              included={includes.aboutUs}
              onIncludedChange={(value) => setInclude("aboutUs", value)}
            >
              <Field label="Opis firmy">
                <AutoResizeTextarea
                  maxLength={300}
                  placeholder="np. Ekipa z 10-letnim doświadczeniem w wykończeniach wnętrz"
                  value={step3Extras?.companyDescription ?? ""}
                  onChange={(value) => patchExtras({ companyDescription: value })}
                />
              </Field>
              <div className={styles.fieldGrid}>
                {[0, 1, 2].map((index) => (
                  <Field key={index} label={`Przewaga firmy ${index + 1}`}>
                    <input
                      className={styles.input}
                      placeholder={COMPANY_ADVANTAGE_PLACEHOLDERS[index]}
                      value={step3Extras?.companyAdvantages?.[index] ?? ""}
                      onChange={(e) => {
                        const next = [...(step3Extras?.companyAdvantages ?? ["", "", ""])];
                        next[index] = e.target.value;
                        patchExtras({ companyAdvantages: next.slice(0, 3) });
                      }}
                    />
                  </Field>
                ))}
              </div>
              <Field label="Referencje / realizacje">
                <AutoResizeTextarea
                  placeholder="np. Remont mieszkania 80m² na Mokotowie — klient polecił nas rodzinie"
                  value={step3Extras?.companyReferences ?? ""}
                  onChange={(value) => patchExtras({ companyReferences: value })}
                />
              </Field>
            </Section>

            <Section
              title="Harmonogram"
              badge="Przekonujaca i Reprezentacyjna"
              included={includes.schedule}
              onIncludedChange={(value) => setInclude("schedule", value)}
            >
              <div className={styles.itemList}>
                {stages.map((stage) => (
                  <div key={stage.id} className={styles.inlineRow}>
                    <input className={styles.input} placeholder="Nazwa etapu" value={stage.name} onChange={(e) => updateStage(stage.id, { name: e.target.value })} />
                    <input className={styles.input} placeholder="Czas" value={stage.duration} onChange={(e) => updateStage(stage.id, { duration: e.target.value })} />
                    <button type="button" className={styles.iconBtn} onClick={() => patchExtras({ scheduleStages: stages.filter((item) => item.id !== stage.id) })} aria-label="Usun etap">
                      <Trash2 aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.addLinkBtn} onClick={addStage} disabled={stages.length >= 6}>
                <Plus aria-hidden /> Dodaj etap
              </button>
            </Section>

            <Section
              title="Proces"
              badge="Przekonujaca i Reprezentacyjna"
              included={includes.process}
              onIncludedChange={(value) => setInclude("process", value)}
            >
              <Field label="Co dzieje sie po akceptacji">
                <AutoResizeTextarea
                  placeholder="np. Kontaktujemy się w ciągu 24h i ustalamy termin zaliczki"
                  value={step3Extras?.afterAcceptance ?? ""}
                  onChange={(value) => patchExtras({ afterAcceptance: value })}
                />
              </Field>
              <Field label="Harmonogram opisowy">
                <AutoResizeTextarea
                  placeholder="np. Tydzień 1–2: prace przygotowawcze. Tydzień 3–4: wykończenia"
                  value={timelineNote ?? ""}
                  onChange={(value) => onTimelineNoteChange(value.trim() ? value : undefined)}
                />
              </Field>
            </Section>
          </>
        ) : null}

        {showPremium ? (
          <>
            {/* TODO: unhide when ≥10 paying users
            <Section title="Warianty realizacji" badge="Reprezentacyjna">
              <label className={styles.toggleLine}>
                <input type="checkbox" checked={step3Extras?.showPriceVariants ?? false} onChange={(e) => patchExtras({ showPriceVariants: e.target.checked })} />
                <span>Pokaz warianty cenowe</span>
              </label>
              {step3Extras?.showPriceVariants ? (
                <div className={styles.variantGrid}>
                  ...
                </div>
              ) : null}
            </Section>
            */}

            <Section
              title="Prowadzenie projektu"
              badge="Reprezentacyjna"
              included={includes.projectManagement}
              onIncludedChange={(value) => setInclude("projectManagement", value)}
            >
              <div className={styles.itemList}>
                {kamienieMilowe.map((kamienMilowy) => (
                  <div key={kamienMilowy.id} className={styles.inlineRow}>
                    <input className={styles.input} placeholder="np. Zakończenie prac malarskich" value={kamienMilowy.name} onChange={(e) => updateKamienMilowy(kamienMilowy.id, { name: e.target.value })} />
                    <input className={styles.input} placeholder="np. Tydzień 3" value={kamienMilowy.dateOrWeek} onChange={(e) => updateKamienMilowy(kamienMilowy.id, { dateOrWeek: e.target.value })} />
                    <input className={styles.input} placeholder="np. 2 000" value={kamienMilowy.payment} onChange={(e) => updateKamienMilowy(kamienMilowy.id, { payment: e.target.value })} />
                    <button type="button" className={styles.iconBtn} onClick={() => patchExtras({ projectMilestones: kamienieMilowe.filter((item) => item.id !== kamienMilowy.id) })} aria-label="Usun kamień milowy">
                      <Trash2 aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.addLinkBtn} onClick={addKamienMilowy} disabled={kamienieMilowe.length >= 8}>
                <Plus aria-hidden /> Dodaj kamień milowy
              </button>
              <Field label="Zasady zglaszania zmian">
                <AutoResizeTextarea
                  placeholder="np. Zmiany zakresu wymagają pisemnego potwierdzenia obu stron"
                  value={step3Extras?.changeRequestRules ?? ""}
                  onChange={(value) => patchExtras({ changeRequestRules: value })}
                />
              </Field>
              <Field label="Standard komunikacji">
                <AutoResizeTextarea
                  placeholder="np. Raport z postępów co tydzień na WhatsApp"
                  value={step3Extras?.communicationStandard ?? ""}
                  onChange={(value) => patchExtras({ communicationStandard: value })}
                />
              </Field>
              <Field label="Odpowiedzialnosci stron">
                <AutoResizeTextarea
                  placeholder="np. Klient zapewnia dostęp do lokalu w godzinach 8–16"
                  value={step3Extras?.partyResponsibilities ?? ""}
                  onChange={(value) => patchExtras({ partyResponsibilities: value })}
                />
              </Field>
            </Section>
          </>
        ) : null}
      </div>

      {step4ClientError ? (
        <p className={styles.alert} role="alert">
          {step4ClientError}
        </p>
      ) : null}
      {step3DraftLimit ? (
        <p className={styles.alert} role="alert">
          Wykorzystano {Math.min(step3DraftLimit.used, 3)}/3 bezplatnych ofert. Przejdz na plan platny, aby
          kontynuowac.{" "}
          <Link to="/pricing" className="font-medium text-foreground underline underline-offset-2 hover:no-underline">
            Zobacz plany →
          </Link>
        </p>
      ) : null}
    </div>
  );
}
